import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { trackEvent } from 'ddc-track-event';

/**
 * Custom hook to handle user events and send tracking data using the `trackEvent` function from `ddc-track-event`.
 *
 * This hook retrieves the `widgetName` and `windowId` from the Redux store and provides a function to send tracking events.
 *
 * @returns {Function} - A function to handle and send tracking events.
 *
 * Usage:
 *
 * const trackEvent = useTrackEvent();
 *
 * const handleClick = () => {
 *   trackEvent({
 *     event: 'button_click',
 *     action: 'click',
 *     value: 'button_value'
 *   });
 * };
 *
 * return (
 *   <button onClick={handleClick}>
 *     Click Me
 *   </button>
 * );
 */
const useTrackEvent = () => {
	const { widgetName, windowId } = useSelector((state) => state.requestData);
	const handleEvent = useCallback(
		(eventData) => {
			trackEvent(widgetName, windowId, {
				widgetName,
				widgetId: windowId,
				event: 'widgetUserEvent',
				action: 'click',
				nonInteractive: false,
				widgetState: 'default',
				value: undefined,
				...eventData
			});
		},
		[widgetName, windowId]
	);

	return handleEvent;
};

export default useTrackEvent;
